import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import { useNavigate } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarQuickFilter,
    GridToolbarExport,
} from '@mui/x-data-grid';

const GoalsGrid = () => {
    const { data: metrics, isLoading, error } = useAuthenticatedData('https://localhost:5001/mos/goals');
    const navigate = useNavigate();

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ backgroundColor: 'black', p: 1 }}>
                <GridToolbarColumnsButton sx={{ color: 'white' }} />
                <GridToolbarFilterButton sx={{ color: 'white' }} />
                <GridToolbarDensitySelector sx={{ color: 'white' }} />
                <GridToolbarQuickFilter 
                    sx={{ 
                        color: 'white', 
                        '& .MuiInputBase-root': { 
                            color: 'white',
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                        },
                        '& .MuiIconButton-root': {
                            color: 'white',
                        },
                    }} 
                />
                <GridToolbarExport sx={{ color: 'white' }} />
            </GridToolbarContainer>
        );
    }

    const handleRowClick = (params) => {
        navigate(`/goal/${params.id}`);
    };

    const getHealthColor = (status) => {
        switch (status) {
            case 'green':
                return 'success';
            case 'yellow':
                return 'warning';
            case 'red':
                return 'error';
            default:
                return 'disabled';
        }
    };

    const columns = [
        {
            field: 'healthStatus',
            headerName: 'Health',
            width: 100,
            renderCell: (params) => (
                <FiberManualRecordIcon color={getHealthColor(params.value)} />
            ),
        },
        { field: 'name', headerName: 'Name', width: 400 },
        { field: 'currentTargetValue', headerName: 'Target', width: 100 },
        { field: 'currentPeriodName', headerName: 'Period', width: 100 },
    ];

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">Error loading goals: {error.message}</Typography>
            </Box>
        );
    }

    if (!metrics || metrics.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>No goals data available.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
                rows={metrics}
                columns={columns}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 20]}
                onRowClick={handleRowClick}
                disableSelectionOnClick
                autoHeight
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                    },
                }}
                sx={{
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'black',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontFamily: "'Roboto Condensed', sans-serif",
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: '#262626',
                    },
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#333333',
                    },
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                        '& button': {
                            color: 'white',
                        },
                        '& .MuiInputBase-root': {
                            color: 'white',
                        },
                    },
                }}
            />
        </Box>
    );
};

export default GoalsGrid;